import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Caption2, FluentProvider, Subtitle1, makeStyles, shorthands, tokens } from '@fluentui/react-components';

import * as React from 'react';
import { useCallback, useEffect } from 'react';
import Chat from './components/chat/Chat';
import { Loading, Login } from './components/views';
import { AuthHelper } from './libs/auth/AuthHelper';
import { useChat, useFile } from './libs/hooks';
import { AlertType } from './libs/models/AlertType';
import { useAppDispatch, useAppSelector } from './redux/app/hooks';
import { RootState } from './redux/app/store';
import { ActiveUserInfo, FeatureKeys } from './redux/features/app/AppState';
import { addAlert, setActiveUserInfo, setServiceInfo } from './redux/features/app/appSlice';
import { semanticKernelDarkTheme, semanticKernelLightTheme } from './styles';

import anleiLogo from './assets/logoanlei.png';
import { Constants } from './Constants';
import { EmptyGuid } from './components/shared/Alerts';
import { ChatService } from './libs/services/ChatService';

export const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        ...shorthands.overflow('hidden'),
    },
    header: {
        alignItems: 'center',
        backgroundColor: tokens.colorBrandShadowAmbient,
        color: tokens.colorPaletteRoyalBlueBorderActive,
        display: 'flex',
        '& h1': {
            paddingLeft: tokens.spacingHorizontalXL,
            display: 'flex',
        },
        height: '48px',
        justifyContent: 'space-between',
        width: '100%',
        '& .branding': {
            display: 'flex',
            alignItems: 'center',
            '& img': {
                marginLeft: '8pt',
                height: '20pt',
            },
        },
    },
    persona: {
        marginRight: tokens.spacingHorizontalXXL,
    },
    cornerItems: {
        display: 'flex',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
});

export enum AppState {
    ProbeForBackend,
    SettingUserInfo,
    ErrorLoadingChats,
    ErrorLoadingUserInfo,
    LoadChats,
    LoadingChats,
    Chat,
    SigningOut,
}

const App = () => {
    const classes = useClasses();
    const [appState, setAppState] = React.useState(AppState.ProbeForBackend);
    const dispatch = useAppDispatch();
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const { features, isMaintenance } = useAppSelector((state: RootState) => state.app);

    const chat = useChat();
    const file = useFile();

    const handleAppStateChange = useCallback((newState: AppState) => {
        setAppState(newState);
    }, []);

    useEffect(() => {
        if (isMaintenance && appState !== AppState.ProbeForBackend) {
            handleAppStateChange(AppState.ProbeForBackend);
            return;
        }

        if (isAuthenticated && appState === AppState.SettingUserInfo) {
            const account = instance.getActiveAccount();

            if (!account) {
                handleAppStateChange(AppState.ErrorLoadingUserInfo);
            } else {
                AuthHelper.getSKaaSAccessToken(instance, inProgress)
                    .then((token) => {
                        const roles = AuthHelper.getRolesFromAccessToken(token);
                        const tenantsRoles = roles.filter((role) => role.toLowerCase() !== 'admin');

                        const firstTenant = tenantsRoles[0]?.split('.')[0] ?? EmptyGuid;

                        const savedTenant =
                            localStorage.getItem(Constants.cacheKeys.LOCAL_CACHE_CURRENT_TENANTID) ?? EmptyGuid;

                        let activeTenantId =
                            savedTenant !== EmptyGuid ? savedTenant : (tenantsRoles[0]?.split('.')[0] ?? EmptyGuid);

                        if (tenantsRoles.length == 1 && firstTenant !== savedTenant) {
                            activeTenantId = tenantsRoles[0]?.split('.')[0] ?? EmptyGuid;
                            localStorage.setItem(Constants.cacheKeys.LOCAL_CACHE_CURRENT_TENANTID, activeTenantId);
                        }

                        const chatService = new ChatService();

                        chatService
                            .getMyTenantsAsync(token)
                            .then((tenants) => {
                                const userInfo: ActiveUserInfo = {
                                    id: `${account.localAccountId}.${account.tenantId}`,
                                    email: account.username,
                                    username: account.name ?? account.username,
                                    activeTenantId: activeTenantId, // Add tenantId property
                                    roles: roles, // Add roles property,
                                    tenants: tenants,
                                };

                                if (process.env.REACT_APP_ENVIRONMENT !== Constants.environment.PRODUCTION) {
                                    console.log('Current User: ', userInfo);
                                }

                                dispatch(setActiveUserInfo(userInfo));

                                if (account.username.split('@')[1] === 'microsoft.com') {
                                    dispatch(
                                        addAlert({
                                            message:
                                                'By using Chat Copilot, you agree to protect sensitive data, not store it in chat, and allow chat history collection for service improvements. This tool is for internal use only.',
                                            type: AlertType.Info,
                                        }),
                                    );
                                }

                                handleAppStateChange(AppState.LoadChats);
                            })
                            .catch((error) => {
                                console.error('Error getting access token', error);
                                handleAppStateChange(AppState.ErrorLoadingUserInfo);
                            });
                    })
                    .catch((error) => {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                        console.error('Error getting access token', error);
                        handleAppStateChange(AppState.ErrorLoadingUserInfo);
                    });
            }
        }

        if ((isAuthenticated || !AuthHelper.isAuthAAD()) && appState === AppState.LoadChats) {
            handleAppStateChange(AppState.LoadingChats);
            void Promise.all([
                chat
                    .loadChats()
                    .then(() => {
                        handleAppStateChange(AppState.Chat);
                    })
                    .catch((error) => {
                        console.error('Error loading chats:', error);
                        handleAppStateChange(AppState.ErrorLoadingChats);
                    }),
                file.getContentSafetyStatus(),
                chat.getServiceInfo().then((serviceInfo) => {
                    if (serviceInfo) {
                        dispatch(setServiceInfo(serviceInfo));
                    }
                }),
            ]);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, inProgress, isAuthenticated, appState, isMaintenance]);

    const theme = features[FeatureKeys.DarkMode].enabled ? semanticKernelDarkTheme : semanticKernelLightTheme;

    return (
        <FluentProvider className="app-container" theme={theme}>
            {AuthHelper.isAuthAAD() ? (
                <>
                    <UnauthenticatedTemplate>
                        <div className={classes.container}>
                            <div className={classes.header} aria-label="Application Header">
                                <div className={'branding'}>
                                    <img src={anleiLogo} />
                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
    <Subtitle1 as="h1">{Constants.app.name}</Subtitle1>
    <Caption2 style={{ paddingTop: '10px' }}>{'v.1.0.0'}</Caption2>
</div>
                                </div>
                            </div>
                            {appState === AppState.SigningOut && <Loading text="Signing you out..." />}
                            {appState !== AppState.SigningOut && <Login />}
                        </div>
                    </UnauthenticatedTemplate>
                    <AuthenticatedTemplate>
                        <Chat classes={classes} appState={appState} setAppState={handleAppStateChange} />
                    </AuthenticatedTemplate>
                </>
            ) : (
                <Chat classes={classes} appState={appState} setAppState={handleAppStateChange} />
            )}
        </FluentProvider>
    );
};

export default App;
